import React from 'react';
import cn from 'classnames';
import type { YourInformationFormState } from './RequestYourInformation';
import RequestYourInformation from './RequestYourInformation';
import type { DetailsFormState } from './RequestDetails';
import RequestDetails from './RequestDetails';
import DocumentationRequirement from './RequestDocumentRequirement';
import { type ExistingUploadedDocumentsByDocumentId, type UploadedDocuments } from './UploadRequiredDocuments';
import { callApi } from '../../util/apiWrapper';
import { METHOD } from '../../constants/method';
import API from '../../constants/api';
import PATH from '../../constants/paths';
import { useHistory } from 'react-router-dom';
import { buildAwardPaymentRequestPayload } from '.';
import type { AwardPaymentRequestContentProps } from './AwardPaymentRequestContent.types';
import {
  normalizePaymentRequestStatus,
  SAVE_ALLOWED_STATUSES,
  SUBMIT_DISABLED_STATUSES
} from './paymentRequestStatuses';
import { AWARD_PAYMENT } from '../../constants/general';
import { INCENTIVE, SCHOLARSHIP } from './paymentRequestTypes';
import { trackAwardPaymentRequestAudit } from '../../util/auditHelper';
import { StatusBanner } from './StatusBanner';

const styles = {
  sectionSpacing: 'pt-8',
  submitContainer: 'pt-8 flex flex-col sm:flex-row sm:justify-between items-center gap-4',
  rightButtons: 'flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto',
  cancelButton: 'w-full sm:w-44 h-12 bg-gray-300 rounded-3xl border-none text-brand-navy text-sm font-bold cursor-pointer hover:shadow-lg transition-shadow',
  submitButton: 'w-full sm:w-44 h-12 bg-blue rounded-3xl border-none text-white text-sm font-bold cursor-pointer hover:shadow-lg transition-shadow',
  saveButton: 'w-full sm:w-44 h-12 bg-light-blue rounded-3xl border-none text-white text-sm font-bold cursor-pointer hover:shadow-lg transition-shadow',
  submitButtonDisabled: 'w-full sm:w-44 h-12 bg-gray-400 rounded-3xl border-none text-white text-sm font-bold cursor-not-allowed',
  errorContainer: 'w-full p-4 bg-red-50 border border-red-300 rounded-lg text-red-800 mb-4',
};

const AwardPaymentRequestContent = ({
  auth,
  initialFormState = null,
  prefilledYourInformationState = null,
  readOnly = false,
  setIsSubmitting: setParentSubmitting
}: AwardPaymentRequestContentProps) => {
  const history = useHistory();
  const isExistingRequest = Boolean(initialFormState && initialFormState.id > 0);
  const isStatusLocked = isExistingRequest && SUBMIT_DISABLED_STATUSES.has(
    normalizePaymentRequestStatus(initialFormState?.status)
  );
  const isFormReadOnly = readOnly || isStatusLocked;

  const [yourInformationState, setYourInformationState] = React.useState<YourInformationFormState | null>(null);
  const [detailsState, setDetailsState] = React.useState<DetailsFormState | null>(null);
  const [uploadedFiles, setUploadedFiles] = React.useState<UploadedDocuments>({});
  const [hasDocumentTypeErrors, setHasDocumentTypeErrors] = React.useState(false);
  const [existingUploadedDocuments, setExistingUploadedDocuments] = React.useState<ExistingUploadedDocumentsByDocumentId>(
    initialFormState?.uploadedDocumentsByDocumentId ?? {}
  );
  const [savedSnapshot, setSavedSnapshot] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  
  React.useEffect(() => {
    setParentSubmitting?.(isSubmitting);
  }, [isSubmitting, setParentSubmitting]);
  
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const currentStatus = normalizePaymentRequestStatus(initialFormState?.status);
  const isNewDraft = !initialFormState || currentStatus === '';
  const isSaveStatusAllowed = isNewDraft || SAVE_ALLOWED_STATUSES.has(currentStatus);
  const isSubmitStatusDisabled = !isSaveStatusAllowed;

  React.useEffect(() => {
    if (error) {
      try {
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (e) {
        // In test environments (jsdom) window.scrollTo may not be implemented — ignore errors
      }
    }
  }, [error]);

  React.useEffect(() => {
    setExistingUploadedDocuments(initialFormState?.uploadedDocumentsByDocumentId ?? {});
  }, [initialFormState]);

  const isFormValid = React.useMemo(() => {
    if (!yourInformationState || !detailsState) return false;

    const { firstName, lastName, enrolledInSchool, employmentStatus } = yourInformationState;
    const { awardType, paymentRecipient, amountRequested, purpose , lastDateOfPayStubs } = detailsState;

    if (!firstName?.trim() || !lastName?.trim() ||  !enrolledInSchool || !employmentStatus || !awardType) return false;

    if (awardType === SCHOLARSHIP) {
      if (!paymentRecipient?.trim() || !amountRequested?.trim() || !purpose?.trim()) {
        return false;
      }
    } else if (awardType === INCENTIVE) {
      if (!lastDateOfPayStubs?.trim()) {
        return false;
      }
    }

    if (enrolledInSchool === AWARD_PAYMENT.ENROLLED_IN_SCHOOL) {
      const { dateOfEnrollment, plannedGraduationDate, gpa } = yourInformationState?.schoolInfo || {};
      if (!dateOfEnrollment?.trim() || !plannedGraduationDate?.trim() || !gpa?.trim()) return false;
    }

    if (employmentStatus === AWARD_PAYMENT.EMPLOYED) {
      const { placeOfEmployment, startDate } = yourInformationState?.employmentInfo || {};
      if (!placeOfEmployment?.trim() || !startDate?.trim()) return false;
    }

    return !hasDocumentTypeErrors;

  }, [yourInformationState, detailsState, hasDocumentTypeErrors]);

  const currentSnapshot = React.useMemo(() => {
    if (!yourInformationState || !detailsState) return null;
    const payload = buildAwardPaymentRequestPayload(
      yourInformationState,
      detailsState,
      initialFormState?.status || AWARD_PAYMENT.DRAFT,
      initialFormState,
      uploadedFiles,
      existingUploadedDocuments
    );

    if (payload instanceof FormData) {
      const item = payload.get('item');
      return typeof item === 'string' ? item : null;
    }

    return JSON.stringify(payload.item);
  }, [detailsState, existingUploadedDocuments, initialFormState, uploadedFiles, yourInformationState]);

  React.useEffect(() => {
    if (savedSnapshot === null && currentSnapshot !== null) {
      setSavedSnapshot(currentSnapshot);
    }
  }, [savedSnapshot, currentSnapshot]);

  const isSaveEnabled = React.useMemo(() => {
    if (isFormReadOnly) return false;
    if (!isSaveStatusAllowed) return false;
    if (currentSnapshot === null || savedSnapshot === null) return false;
    if (hasDocumentTypeErrors) return false;
    return currentSnapshot !== savedSnapshot && !isSubmitting;
  }, [currentSnapshot, savedSnapshot, isSubmitting, isFormReadOnly, isSaveStatusAllowed, hasDocumentTypeErrors]);

  const handleSave = async () => {
    if (isFormReadOnly) return;
    if (!yourInformationState || !detailsState) return;
    if (hasDocumentTypeErrors) {
      setError('Please select a document type for each uploaded file before saving the request.');
      return;
    }

    setSubmitAttempted(false);
    setError(null);
    setIsSubmitting(true);

    try {
      const yourInfoWithUserId = {
        ...yourInformationState,
        userId: yourInformationState?.userId ?? initialFormState?.userId ?? prefilledYourInformationState?.userId ?? 0,
      } as YourInformationFormState;
      const payload = buildAwardPaymentRequestPayload(
        yourInfoWithUserId,
        detailsState,
        initialFormState?.status?.toLowerCase()  || AWARD_PAYMENT.DRAFT,
        initialFormState,
        uploadedFiles,
        existingUploadedDocuments
      );
      const requestMethod = isExistingRequest ? METHOD.PUT : METHOD.POST;
      await trackAwardPaymentRequestAudit({ auth, payload, action: 'save', requestMethod });
      const response = await callApi(auth, API.PAYMENT_REQUEST, requestMethod, payload);
      const resultStatus = (response as any)?.result?.status?.toLowerCase();
      const httpStatus = (response as any)?.status;
      if (httpStatus === 200 || httpStatus === 201 || resultStatus ===  initialFormState?.status?.toLowerCase() || resultStatus === AWARD_PAYMENT.DRAFT) {
        navigateCancel();
      }
      if (isMountedRef.current) {
        setSavedSnapshot(currentSnapshot);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const isSubmitEnabled = !isSubmitting && !isSubmitStatusDisabled && !isFormReadOnly && !hasDocumentTypeErrors;

  const handleSubmit = async () => {
    if (isFormReadOnly) return;
    if (isSubmitStatusDisabled) return;
    if (hasDocumentTypeErrors) {
      setError('Please select a document type for each uploaded file before submitting the request.');
      return;
    }
    setSubmitAttempted(true);
    if (!isFormValid || !yourInformationState || !detailsState) {
      setError('Please fill in all required fields');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const yourInfoWithUserId = {
        ...yourInformationState,
        userId: yourInformationState?.userId ?? initialFormState?.userId ?? prefilledYourInformationState?.userId ?? 0,
      } as YourInformationFormState;
      const payload = buildAwardPaymentRequestPayload(
        yourInfoWithUserId,
        detailsState,
        AWARD_PAYMENT.UNDER_REVIEW,
        initialFormState,
        uploadedFiles,
        existingUploadedDocuments
      );
      const requestMethod = isExistingRequest ? METHOD.PUT : METHOD.POST;
      await trackAwardPaymentRequestAudit({ auth, payload, action: 'submit', requestMethod });
      const response = await callApi(auth, API.PAYMENT_REQUEST, requestMethod, payload);
      const responseStatus = (response as any)?.result?.status?.toLowerCase();
      history?.push(PATH.AWARD_PAYMENT_RESPONSE + `?status=${responseStatus === AWARD_PAYMENT.UNDER_REVIEW || response.status === 200 || response.status === 201 ? AWARD_PAYMENT.SUCCEEDED : AWARD_PAYMENT.FAILED}`);
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const navigateCancel = () => {
    history?.push(PATH.AWARD_PAYMENT_REQUESTS);
  };

  return (
    <>
     <StatusBanner status={initialFormState?.status} />
      {error && <div className={cn(styles.errorContainer)}>{error}</div>}
      <DocumentationRequirement />
      <div className={cn(styles.sectionSpacing)}>
        <RequestYourInformation
          initialValues={initialFormState?.yourInformationState ?? prefilledYourInformationState}
          readOnly={isFormReadOnly}
          showRequiredErrors={submitAttempted}
          onFormChange={setYourInformationState}
        />
      </div>
      <div className={cn(styles.sectionSpacing)}>
        <RequestDetails
          initialValues={initialFormState?.detailsState}
          readOnly={isFormReadOnly}
          showRequiredErrors={submitAttempted}
          onFormChange={setDetailsState}
          onUploadedFilesChange={setUploadedFiles}
          onExistingUploadedDocumentsChange={setExistingUploadedDocuments}
          onDocumentValidationChange={setHasDocumentTypeErrors}
          initialUploadedDocuments={existingUploadedDocuments}
          initialUploadedFiles={uploadedFiles}
        />
      </div>
      <div className={cn(styles.submitContainer)}>
        <button type='button' className={cn(styles.cancelButton)} onClick={navigateCancel}>
          {isFormReadOnly && !isStatusLocked ? 'Back' : 'Cancel'}
        </button>
        {(!readOnly || isStatusLocked) && (
          <div className={cn(styles.rightButtons)}>
            <button
              type='button'
              className={cn(isSaveEnabled ? styles.saveButton : styles.submitButtonDisabled)}
              onClick={handleSave}
              disabled={!isSaveEnabled}
            >
              Save Request
            </button>
            <button
              type='button'
              className={cn(isSubmitEnabled ? styles.submitButton : styles.submitButtonDisabled)}
              onClick={handleSubmit}
              disabled={!isSubmitEnabled}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default AwardPaymentRequestContent;
